/**
 * lib/tileIdentity.js — canonical tile identity + generation invalidation (IP-001 Step 5).
 *
 * Every tile key MUST include tenant context / sheet id / revision id /
 * pyramid generation / level / x / y. Generation is bumped on any sheet
 * OR revision change; late callbacks arriving after a generation change
 * are DETECTABLE and REJECTABLE via `isStaleGeneration`.
 *
 * Pure functions. No React, no DOM, no storage.
 */

/** Build a canonical tile key. Any field mismatch = different tile. */
export function tileKey({ tenantId, sheetId, revisionId, generation, level, x, y }) {
  if (!sheetId) throw new Error("tileKey requires sheetId");
  if (generation == null) throw new Error("tileKey requires generation");
  if (level == null || x == null || y == null) throw new Error("tileKey requires level/x/y");
  return `${tenantId || "_"}::${sheetId}::${revisionId || "r0"}::g${generation}::L${level}::${x}_${y}`;
}

/** Parse a tile key back into its components. Returns null on malformed. */
export function parseTileKey(key) {
  const m = /^([^:]+)::([^:]+)::([^:]+)::g(\d+)::L(\d+)::(\d+)_(\d+)$/.exec(key || "");
  if (!m) return null;
  return {
    tenantId: m[1] === "_" ? null : m[1],
    sheetId: m[2],
    revisionId: m[3],
    generation: parseInt(m[4], 10),
    level: parseInt(m[5], 10),
    x: parseInt(m[6], 10),
    y: parseInt(m[7], 10),
  };
}

/**
 * Track the currently authoritative generation per (tenantId, sheetId).
 * Any sheet or revision change bumps the generation and returns the new
 * value. Callers stamp outgoing requests with the returned generation
 * and check `isStaleGeneration` in every completion handler.
 */
export function makeGenerationBook() {
  const store = new Map(); // key = tenant::sheet, value = { generation, revisionId }

  function keyOf(tenantId, sheetId) {
    return `${tenantId || "_"}::${sheetId}`;
  }
  return {
    /** Current generation for a scope. Missing scope returns null. */
    get(tenantId, sheetId) {
      const e = store.get(keyOf(tenantId, sheetId));
      return e ? e.generation : null;
    },
    /** Ensure a scope has a generation. Returns the (possibly new) value. */
    ensure(tenantId, sheetId, revisionId) {
      const k = keyOf(tenantId, sheetId);
      let e = store.get(k);
      if (!e) {
        e = { generation: 1, revisionId: revisionId || "r0" };
        store.set(k, e);
      } else if (e.revisionId !== (revisionId || "r0")) {
        // Revision changed under the same sheet — bump generation.
        e.generation += 1;
        e.revisionId = revisionId || "r0";
      }
      return e.generation;
    },
    /** Force a generation bump (used on sheet switch or explicit invalidate). */
    bump(tenantId, sheetId, revisionId) {
      const k = keyOf(tenantId, sheetId);
      const e = store.get(k) || { generation: 0, revisionId: revisionId || "r0" };
      e.generation += 1;
      e.revisionId = revisionId || "r0";
      store.set(k, e);
      return e.generation;
    },
    /**
     * True if the given (scope, generation) is behind the current
     * generation. Callers use this to drop late onLoad/onError callbacks
     * that were issued against a since-superseded sheet/revision.
     */
    isStaleGeneration(tenantId, sheetId, generation) {
      const e = store.get(keyOf(tenantId, sheetId));
      if (!e) return true; // no scope = anything is stale
      return generation < e.generation;
    },
    /** Drop all state — used on tenant switch / logout. */
    clear() { store.clear(); },
  };
}

/**
 * Given a candidate list of visible-tile identities, count violations
 * of the IP-001 tile-hygiene invariants:
 *   duplicates    — same key appears more than once
 *   staleGen      — key generation is behind the current one
 *   crossSheet    — key sheetId does not match the current sheetId
 *   crossRevision — key revisionId does not match the current revisionId
 */
export function auditVisibleTiles(keys, { currentTenantId, currentSheetId,
                                          currentRevisionId, currentGeneration }) {
  const seen = new Set();
  let duplicates = 0, staleGen = 0, crossSheet = 0, crossRevision = 0, malformed = 0;
  for (const k of keys) {
    if (seen.has(k)) duplicates += 1;
    seen.add(k);
    const p = parseTileKey(k);
    if (!p) { malformed += 1; continue; }
    if (currentSheetId && p.sheetId !== currentSheetId) crossSheet += 1;
    if (currentRevisionId && p.revisionId !== currentRevisionId) crossRevision += 1;
    if (currentGeneration != null && p.generation < currentGeneration) staleGen += 1;
    void currentTenantId; // reserved for future multi-tenant assertions
  }
  return { duplicates, staleGen, crossSheet, crossRevision, malformed };
}
